import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { ConfirmPassDeletionModal } from "@/pages/mill/carriers/components/passes/ConfirmPassDeletionModal";
import type {
	AffectedPassChange,
	PassChangeSummary,
} from "@/pages/mill/carriers/repositories/CarrierAssemblyRepository";
import { previewPassChangesOutsideWindow } from "@/pages/mill/carriers/repositories/CarrierAssemblyRepository";
import { WarningModal } from "@/shared/modals/WarningModal";
import { RollsetSelector } from "@/shared/selectors";
import { CarrierSelector } from "@/shared/selectors/CarrierSelector";
import { SectionSelector } from "@/shared/selectors/SectionSelector";
import {
	ErrorModal,
	getMomentTz,
	HelmetDexteel,
	MesfModal,
	TimeService,
} from "@dexteel/mesf-core";
import {
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Grid2,
	Typography,
	useTheme,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { upsertCarrierFromGantt } from "../../repositories/repositories";
import { installData } from "../GanttChart";

interface InstallForm {
	InstalledAt: Date | null;
	UninstalledAt: Date | null;
	SectionId: number | null;
	PartId: number | null;
	RollsetId: number | null;
}

const moment = getMomentTz();

type Props = {
	show: boolean;
	data: installData | null;
	onHide: (update?: boolean) => void;
	suffixTitle?: string;
};

export const CarrierInstallationModal = ({
	show,
	data,
	onHide,
	suffixTitle,
}: Props) => {
	const theme = useTheme();

	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");
	const [warningMessage, setWarningMessage] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [showUninstalledAt, setShowUninstalledAt] = useState(false);

	// Pass deletion confirmation state
	const [showConfirmDeletion, setShowConfirmDeletion] = useState(false);
	const [affectedPasses, setAffectedPasses] = useState<AffectedPassChange[]>(
		[],
	);
	const [passSummary, setPassSummary] = useState<PassChangeSummary[]>([]);

	const { control, reset, setValue, watch, trigger } = useForm<InstallForm>({
		mode: "onSubmit",
	});

	const watchedInstalledAt = watch("InstalledAt");
	const watchedUninstalledAt = watch("UninstalledAt");
	const watchedSectionId = watch("SectionId");
	const watchedPartId = watch("PartId");
	const watchedRollsetId = watch("RollsetId");

	const doUpsert = (forceDeletePasses?: boolean) => {
		setIsSubmitLoading(true);
		upsertCarrierInstance.mutate(forceDeletePasses);
	};

	const upsertCarrierInstance = useMutation(
		(forceDeletePasses?: boolean) => {
			return upsertCarrierFromGantt({
				carrierInstanceId: data!.carrierInstanceId!,
				partId: watchedPartId!,
				rollsetId: watchedRollsetId!,
				sectionId: watchedSectionId!,
				standId: data?.activeStandId!,
				installedAt: watchedInstalledAt!,
				uninstalledAt: watchedUninstalledAt!,
				forceDeletePasses,
			});
		},
		{
			onSuccess: (warningMessage?: string) => {
				setWarningMessage(warningMessage || "");
				onHide(true);
			},
			onError: (error: Error) => {
				setError(error.message);
			},
			onSettled: () => setIsSubmitLoading(false),
		},
	);

	const onSave = async () => {
		const isValid = await trigger();
		if (!isValid) return;

		// Check if this is an edit - preview passes outside the new window
		if (
			data?.carrierInstanceId &&
			(watchedInstalledAt || watchedUninstalledAt)
		) {
			try {
				setIsSubmitLoading(true);
				const preview = await previewPassChangesOutsideWindow({
					carrierInstanceId: data.carrierInstanceId,
					proposedInstalledAt: watchedInstalledAt ?? undefined,
					proposedUninstalledAt: watchedUninstalledAt ?? undefined,
				});

				if (preview.affectedPasses.length > 0) {
					setAffectedPasses(preview.affectedPasses);
					setPassSummary(preview.summary);
					setShowConfirmDeletion(true);
					setIsSubmitLoading(false);
					return;
				}
			} catch (err: any) {
				setError(err.message || "Error checking pass changes");
				setIsSubmitLoading(false);
				return;
			}
		}

		doUpsert();
	};

	const handleConfirmDeletion = () => {
		setShowConfirmDeletion(false);
		doUpsert(true);
	};

	const handleCancel = () => {
		onHide();
	};

	useEffect(() => {
		if (show && data) {
			reset({
				InstalledAt: null,
				UninstalledAt: null,
				SectionId: null,
				PartId: null,
				RollsetId: null,
			});
			if (data.clickedTime) {
				const clickedDate = new Date(data.clickedTime);
				const presetUninstalledAt = new Date(clickedDate);
				presetUninstalledAt.setHours(presetUninstalledAt.getHours() + 3);

				setValue("InstalledAt", clickedDate);
				setValue(
					"UninstalledAt",
					presetUninstalledAt > new Date() ? new Date() : presetUninstalledAt,
				);
				setShowUninstalledAt(true);
			} else if (data.installedat) {
				setValue("InstalledAt", data.installedat);
				setValue("UninstalledAt", data.uninstalledat || null);
				setShowUninstalledAt(data.uninstalledat ? true : false);
			} else {
				setShowUninstalledAt(false);
			}
			setValue("SectionId", data.activeSectionId || null);
			setValue("PartId", data.partId || null);
			setValue("RollsetId", data.rollsetId || null);
		}
	}, [show, data]);

	const validateNotInFuture = (description: string, value: Date | null) => {
		if (value && value > new Date()) {
			return `${description} time cannot be in the future.`;
		}
		return true;
	};

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`${data?.clickedTime ? "Install" : "Edit"} Carrier on ${
						data?.activeStand
					} - ${suffixTitle}`}
				/>
			)}
			<MesfModal
				open={show}
				handleClose={handleCancel}
				title={`${
					data?.carrierInstanceId ? "Edit" : "Install"
				} Carrier on Stand ${data?.activeStand || ""}`}
				maxWidth="xl"
			>
				<MesfModal.Content
					dividers
					sx={{
						padding: (theme) => theme.spacing(3),
						minWidth: 400,
					}}
				>
					<div
						style={{
							marginBottom: theme.spacing(3),
							padding: theme.spacing(2),
							backgroundColor: theme.palette.grey[50],
							borderRadius: theme.spacing(1),
							border: `1px solid ${theme.palette.grey[300]}`,
						}}
					>
						<Grid2 container spacing={5} alignItems="center">
							<Grid2>
								<Typography
									fontWeight={"bold"}
									sx={{
										marginBottom: theme.spacing(2),
									}}
								>
									Carrier Installation Details
								</Typography>
							</Grid2>
							<Grid2 style={{ marginTop: -15 }}>
								<FormControlLabel
									control={
										<Checkbox
											checked={showUninstalledAt}
											onChange={(e) => {
												setShowUninstalledAt(e.target.checked);
												if (e.target.checked) {
													if (data?.clickedTime) {
														const clickedDate = new Date(data.clickedTime);
														const presetUninstalledAt = new Date(clickedDate);
														presetUninstalledAt.setHours(
															presetUninstalledAt.getHours() + 3,
														);

														setValue(
															"UninstalledAt",
															presetUninstalledAt > new Date()
																? new Date()
																: presetUninstalledAt,
														);
													} else if (data?.uninstalledat) {
														setValue("UninstalledAt", data.uninstalledat);
													}
												} else {
													setValue("UninstalledAt", null);
												}
											}}
											color="primary"
										/>
									}
									label="Have Uninstalled At"
								/>
							</Grid2>
						</Grid2>
						<Grid2 container spacing={3}>
							<Grid2 size={{ md: 6 }}>
								<Controller
									name="InstalledAt"
									control={control}
									shouldUnregister
									rules={{
										required: "Installed At is required",
										validate: {
											notInFuture: (value) =>
												validateNotInFuture("Installed At", value),
										},
									}}
									render={({ field, fieldState: { error } }) => (
										<DateTimePicker
											label="Installed At"
											value={field.value ? moment(field.value) : null}
											onChange={field.onChange}
											timezone={TimeService.getInstance().getServerTimeZone()}
											format="MM/DD/YYYY HH:mm:ss"
											ampm={false}
											slotProps={{
												textField: {
													size: "small",
													fullWidth: true,
													required: true,
													helperText: error?.message,
													error: !!error,
												},
											}}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 6 }}>
								<Controller
									name="UninstalledAt"
									control={control}
									shouldUnregister
									rules={{
										validate: {
											notInFuture: (value) =>
												validateNotInFuture("Uninstalled At", value),
										},
									}}
									render={({ field, fieldState: { error } }) => (
										<DateTimePicker
											label="Uninstalled At"
											value={field.value ? moment(field.value) : null}
											onChange={field.onChange}
											timezone={TimeService.getInstance().getServerTimeZone()}
											format="MM/DD/YYYY HH:mm:ss"
											ampm={false}
											disabled={!showUninstalledAt}
											slotProps={{
												textField: {
													size: "small",
													fullWidth: true,
													required: showUninstalledAt,
													helperText: error?.message,
													error: !!error,
												},
											}}
										/>
									)}
								/>
							</Grid2>

							<Grid2 size={{ xs: 12 }}>
								<Controller
									name="SectionId"
									control={control}
									rules={{
										required: "Section is required",
									}}
									render={({ field, fieldState: { error } }) => (
										<SectionSelector
											label="Section"
											value={field.value}
											onChange={field.onChange}
											fieldError={error}
											disabled={!!data?.partId}
											required
										/>
									)}
								/>
							</Grid2>

							<Grid2 size={{ xs: 12 }}>
								<Controller
									name="PartId"
									control={control}
									shouldUnregister
									rules={{
										required: "Carrier is required",
									}}
									render={({ field, fieldState: { error } }) => (
										<CarrierSelector
											label="Carrier"
											value={field.value}
											onChange={field.onChange}
											fieldError={error}
											required
											disabled={!watchedSectionId || !!data?.partId}
										/>
									)}
								/>
							</Grid2>

							<Grid2 size={{ xs: 12 }}>
								<Controller
									name="RollsetId"
									control={control}
									shouldUnregister
									rules={{
										required: "Rollset is required",
									}}
									render={({ field, fieldState: { error } }) => (
										<RollsetSelector
											label="Rollset"
											value={field.value}
											onChange={field.onChange}
											standId={data?.activeStandId!}
											standName={data?.activeStand!}
											sectionId={watchedSectionId}
											disabled={
												!watchedSectionId || !watchedPartId || !!data?.partId
											}
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</div>
				</MesfModal.Content>

				<MesfModal.Actions style={{ padding: "15px 30px" }}>
					<Grid2 container spacing={2} justifyContent="flex-end">
						<Grid2 size={{ md: 3 }}>
							<Button
								variant="contained"
								color="inherit"
								onClick={handleCancel}
								disabled={isSubmitLoading}
								fullWidth
							>
								Cancel
							</Button>
						</Grid2>
						<Grid2 size={{ md: 3 }}>
							<Button
								variant="contained"
								color="primary"
								onClick={onSave}
								startIcon={
									isSubmitLoading ? (
										<CircularProgress size="1rem" color="inherit" />
									) : null
								}
								disabled={isSubmitLoading}
								fullWidth
							>
								Save
							</Button>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>

			<ErrorModal error={error} onHide={() => setError("")} />
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
			<WarningModal
				open={warningMessage !== ""}
				title="Warning"
				message={warningMessage}
				onClose={() => setWarningMessage("")}
				suffixTitle="Gantt"
			/>
			<ConfirmPassDeletionModal
				open={showConfirmDeletion}
				onClose={() => setShowConfirmDeletion(false)}
				onConfirm={handleConfirmDeletion}
				affectedPasses={affectedPasses}
				summary={passSummary}
			/>
		</>
	);
};
